import React, { useState } from 'react';
import { Alert } from '@mui/material';
import { motion } from 'framer-motion';
import { Box } from '@mui/material';
import UrlInput from '../../components/UrlInput';
import VideoInfo from '../../components/VideoInfo';
import DownloadHistory from '../../components/DownloadHistory';
import { VideoMetadata } from '../../services/api';
import { useDownloadHistory } from '../../hooks/useDownloadHistory';

const DownloaderPage: React.FC = () => {
  const [videoInfo, setVideoInfo] = useState<VideoMetadata | null>(null);
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const { history, addToHistory, clearHistory, deleteItem } = useDownloadHistory();

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        staggerChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const handleVideoInfo = (info: VideoMetadata) => {
    setVideoInfo(info);
    setVideoUrl(info.url);
    setError(null);
    addToHistory(info);
  };

  const handleHistoryItemClick = (item: VideoMetadata) => {
    setVideoInfo(item);
    setVideoUrl(item.url);
    setError(null);
    // Smoothly scroll to top of page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteItem = (item: VideoMetadata) => {
    deleteItem(item);
    // If the currently displayed video is being deleted, clear it
    if (videoInfo && videoInfo.url === item.url) {
      setVideoInfo(null);
      setVideoUrl('');
    }
  };

  return (
    <Box
      component={motion.div}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 2
      }}
    >
      <motion.div variants={itemVariants}>
        <UrlInput 
          onVideoInfo={handleVideoInfo}
          setError={setError} 
        />
      </motion.div>
      
      {error && (
        <motion.div variants={itemVariants}>
          <Alert 
            severity="error" 
            className="mb-4"
            onClose={() => setError(null)}
            sx={{
              backgroundColor: '#2D1115',
              color: '#F4212E',
              '& .MuiAlert-icon': {
                color: '#F4212E',
              },
            }}
          >
            {error}
          </Alert>
        </motion.div>
      )}
      {videoInfo && (
        <motion.div variants={itemVariants}>
          <VideoInfo 
            videoInfo={videoInfo} 
            videoUrl={videoUrl} 
            setError={setError}
            onDelete={() => handleDeleteItem(videoInfo)}
          />
        </motion.div>
      )}

      <motion.div variants={itemVariants}>
        <DownloadHistory
          history={history}
          onItemClick={handleHistoryItemClick}
          onClearHistory={clearHistory}
          onDeleteItem={handleDeleteItem}
        />
      </motion.div>
    </Box>
  );
};

export default DownloaderPage; 